import { Button } from '@src/components/Button';
import { ButtonType } from '@common/types/button';
import { type InvitedRecruiter } from '@src/api/services/RecruiterService';
import { RecruiterItem } from './RecruiterItem';
import { useCallback, useMemo, useState } from 'react';
import { useFormatValidUntil } from '@src/hooks/useFormatValidUntil';
import { useTranslation } from 'react-i18next';

type InvitedRecruiterItem = {
  recruiter: InvitedRecruiter;
  onDelete?: () => Promise<void>;
  disabled?: boolean;
  onResendInvite?: () => Promise<void>;
};

export const InvitedRecruiterItem = ({
  recruiter,
  onDelete,
  disabled,
  onResendInvite,
}: InvitedRecruiterItem) => {
  const { validUntilWithText } = useFormatValidUntil({
    expiresAt: recruiter.expiresAt,
  });

  const { t } = useTranslation('recruiterView');
  const [inviteSending, setInviteSending] = useState(false);

  const displayName = useMemo(() => {
    const splittedMail = recruiter.email.split('@');

    return (splittedMail[0] ?? recruiter.email) + ` (${validUntilWithText})`;
  }, [recruiter.email, validUntilWithText]);

  const handleResendInvite = useCallback(async () => {
    if (!onResendInvite) {
      return;
    }
    setInviteSending(true);
    const startTime = Date.now();
    try {
      await onResendInvite();
    } finally {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(1500 - elapsedTime, 0);
      setTimeout(() => setInviteSending(false), remainingTime);
    }
  }, [onResendInvite]);

  return (
    <RecruiterItem
      avatar={null}
      displayName={displayName}
      email={recruiter.email}
      onDelete={onDelete}
      disabled={disabled}
    >
      {onResendInvite && (
        <Button
          buttonType={ButtonType.SECONDARY}
          label={t('resendInvite')}
          onClick={handleResendInvite}
          loading={inviteSending}
        />
      )}
    </RecruiterItem>
  );
};
